import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Dropdown, Menu, Tag } from 'antd';
import { capitalCase } from 'change-case';
import { TbDots, TbPyramid, TbTrash, TbUserCircle, TbUserHexagon, TbUserMinus, TbUserPentagon } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { teamService } from '@/services';
import { DropdownMenuPanel, VirtualizedList as List, MiddleDot } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { useResourceRoutePreview } from '@/shared/components/resource-route-preview/context';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores';
import { Team, Teammate, TeammatePermissionType } from '@/types';
import { formatDate } from '@/utilities';

interface MemberListItemProps {
    team: Team;
    collaborator: Teammate;
    collaborators: Teammate[];
    setCollaborators: any;
}

const MemberListItem: FC<MemberListItemProps> = ({
    team,
    collaborator,
    collaborators,
    setCollaborators,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);
    const preview = useResourceRoutePreview();

    const { selectedUser: user } = useUserStore();

    const [permission, setPermission] = useState(collaborator?.permission ? collaborator?.permission : '');

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();

    const handleRemoveTeamCollaboratorFromTeam = async () => {
        if (preview) {
            return;
        }
        try {
            await teamService.removeCollaborator(team.id, collaborator.id);

            const filtered = collaborators?.filter((c) => {
                return c?.id !== collaborator?.id;
            });

            setCollaborators(filtered);

            toast('Removed from Team', {
                icon: <TbUserMinus size='1rem' />,
                description: `${collaborator?.member?.user?.firstName} ${collaborator?.member?.user?.lastName}`,
            });
        } catch (error) {
            captureException(error);
        }
    };

    const handleTeamCollaboratorPermissionUpdate = async (key: any) => {
        if (preview) {
            return;
        }
        try {
            const teammate = await teamService.updateCollaborator(team.id, collaborator.id, { permission: key });

            setPermission(teammate.permission ?? '');

            toast('Teammate Permission Updated', {
                icon: <TbPyramid size='1rem' />,
                description: teammate.permission,
            });
        } catch (error) {
            captureException(error);
        }
    };

    return (
        <>
            <BaseSkeleton
                avatar
                title={false}
                loading={loading}
                active
                style={{ marginTop: '2rem' }}
                paragraph={{ rows: 2 }}
            >
                <List.Item
                    extra={
                        preview ? null : (
                            <>
                                <Dropdown
                                    trigger={['click']}
                                    placement='bottomRight'
                                    popupRender={() => (
                                        <DropdownMenuPanel>
                                            <Menu.Item
                                                danger
                                                icon={<TbTrash size={'1rem'} />}
                                                onClick={() => {
                                                    handleRemoveTeamCollaboratorFromTeam();
                                                }}
                                                disabled={collaborator?.permission === TeammatePermissionType.OWNER}
                                            >
                                                Remove from Team
                                            </Menu.Item>
                                        </DropdownMenuPanel>
                                    )}
                                >
                                    <Button
                                        icon={
                                            <TbDots
                                                size={'1rem'}
                                                style={{ verticalAlign: '-0.125rem', color: token.colorTextTertiary }}
                                            />
                                        }
                                        type='text'
                                    />
                                </Dropdown>
                            </>
                        )
                    }
                >
                    <List.Item.Meta
                        avatar={
                            <>
                                <Avatar src={collaborator?.member?.user?.avatar} style={{ verticalAlign: '-1.5rem' }} />
                            </>
                        }
                        title={
                            <>
                                {collaborator?.member?.user?.firstName} {collaborator?.member?.user?.lastName}
                                {collaborator?.member?.user?.username && (
                                    <small style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>
                                        @{collaborator?.member?.user?.username}
                                    </small>
                                )}
                                {user?.id === collaborator?.member?.user?.id ? (
                                    <Tag style={{ borderColor: 'transparent', marginLeft: '0.5rem' }}>Owner</Tag>
                                ) : undefined}
                            </>
                        }
                        description={
                            <>
                                {collaborator?.member?.user?.emailAddress}
                                <MiddleDot horizontalMargin={'0.5rem'} />
                                Joined {collaborator?.createdAt ? formatDate(collaborator?.createdAt) : ''}
                            </>
                        }
                    />
                    <Dropdown
                        disabled={Boolean(preview) || user?.id === collaborator?.member?.user?.id}
                        trigger={['click']}
                        overlayStyle={{ minWidth: 16 * 12 }}
                        placement='bottomRight'
                        popupRender={() => (
                            <DropdownMenuPanel
                                selectedKeys={[permission]}
                                onClick={(e: any) => {
                                    handleTeamCollaboratorPermissionUpdate(e.key);
                                }}
                            >
                                <Menu.Item key={TeammatePermissionType.MEMBER}>
                                    <TbUserHexagon
                                        size='1rem'
                                        style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                    />
                                    Member
                                    <br />
                                    <small style={{ color: token.colorTextTertiary }}>
                                        Ability to view, collaborate, and invite members to the team.
                                    </small>
                                </Menu.Item>
                                <Menu.Item key={TeammatePermissionType.ADMIN}>
                                    <TbUserPentagon
                                        size='1rem'
                                        style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                    />
                                    Admin
                                    <br />
                                    <small style={{ color: token.colorTextTertiary }}>
                                        Full access to investments and other resources on the team.
                                    </small>
                                </Menu.Item>
                                <Menu.Item key={TeammatePermissionType.GUEST}>
                                    <TbUserCircle
                                        size='1rem'
                                        style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                    />
                                    Guest
                                    <br />
                                    <small style={{ color: token.colorTextTertiary }}>
                                        Access limited to select items on the team.
                                    </small>
                                </Menu.Item>
                                {user?.id === collaborator?.member?.user?.id ? (
                                    <>
                                        <Menu.Divider />
                                        <Menu.Item key={TeammatePermissionType.OWNER}>Owner</Menu.Item>
                                    </>
                                ) : undefined}
                            </DropdownMenuPanel>
                        )}
                    >
                        <DropdownButton type='dashed' style={{ marginRight: '1rem' }}>
                            {capitalCase(permission)}
                        </DropdownButton>
                    </Dropdown>
                </List.Item>
            </BaseSkeleton>
        </>
    );
};

export default MemberListItem;
